import { Link, useMatches, UIMatch, Params } from "react-router";
import * as React from "react";
import {memo, useContext} from "react";
import { Helmet } from "react-helmet-async";
import { AppContext, AppContextType } from "../../context/AppContext";

/**
 * handle type
 */
type HandleFunc = (route?: HandleType['route'], context?: AppContextType, params?: Params<string>) => string;
type HandleType = {
  route: {
    title: string,
    icon: string,
    id: string
  };
  breadcrumb?: string | HandleFunc;
};

/**
 * breadcrumb type
 */
export type BreadcrumbType = {
  path: string,
  label: string | undefined,
  id: string
};

/**
 * BreadcrumbCustom
 */
let BreadcrumbMatches: UIMatch<unknown, unknown>[] = [],
  BreadcrumbContext: AppContextType,
  BreadcrumbStringSeperator: string = "|";

/**
 * processBreadcrumb
 * @param matches
 * @param Context
 * @param matchHandler
 */
const processBreadcrumb = (
  matches: UIMatch<unknown, unknown>[],
  Context: AppContextType,
  matchHandler?: (match: any) => any
): BreadcrumbType[] => {
  let breadcrumbs: BreadcrumbType[] = matches
      // ensures that only matches with a breadcrumb are included
      .filter((match: UIMatch<unknown, unknown>): match is UIMatch<unknown, HandleType> => !!match.handle && !!(match.handle as HandleType).breadcrumb)
      .map((match: UIMatch<unknown, HandleType>): BreadcrumbType => {
        if (matchHandler) {
          match = matchHandler(match);
        }

        return {
          path: match.pathname,
          label: matchHandler ? match.handle.route.title : match.handle.breadcrumb && typeof match.handle.breadcrumb !== "string" ? match.handle.breadcrumb(match.handle.route, Context, match.params) : match.handle.breadcrumb,
          id: match.handle.route.id
        }
      });

  Context.setBreadcrumbs(breadcrumbs);

  return breadcrumbs;
};

/**
 * renderBreadcrumbLabel
 *
 * composed titles carry the "|" separator inside the label string; as plain
 * text it lands in the h1's accessible name ("Client bearbeiten | Johannes
 * Test"). rendered as a decorative element it stays visible but leaves only
 * the real title parts to be read
 * @param label
 */
const renderBreadcrumbLabel = (label: string | undefined): React.ReactNode => {
  return String(label ?? "").split(BreadcrumbStringSeperator).map((part: string, index: number) => (
    <React.Fragment key={ index }>
      { index > 0 && <span data-breadcrumb-separator aria-hidden="true">{ BreadcrumbStringSeperator }</span> }
      { part }
    </React.Fragment>
  ));
};

/**
 * renderBreadcrumb
 * @param Context
 */
const renderBreadcrumb = (Context: AppContextType): JSX.Element[] => {
  let breadcrumbs: BreadcrumbType[] = Context.getBreadcrumbs(),
    breadcrumbElements: JSX.Element[] = [];

  // render all breadcrumbs by path and label
  breadcrumbs.forEach((breadcrumb: BreadcrumbType, index: number): void => {
    const isLast: boolean = index === breadcrumbs.length - 1;

    breadcrumbElements.push(isLast ?
      // the current page's crumb is the only visible page title, so it carries
      // the document's h1; CSS keeps it visually identical to the plain text
      <li key={ breadcrumb.path } aria-current="page">&nbsp;<h1>{ renderBreadcrumbLabel(breadcrumb.label) }</h1> </li> :
      <li key={ breadcrumb.path }>
        &nbsp;<Link to={ breadcrumb.path } viewTransition>{ renderBreadcrumbLabel(breadcrumb.label) }</Link>
      </li>
    );
  });

  return breadcrumbElements;
};

/**
 * setBreadcrumb
 * @param routeId
 * @param title
 * @param mode
 */
export const setBreadcrumb = (routeId: string, title: string, mode: "replace" | "prepend" | "append" = "replace")=> {
  processBreadcrumb(
    BreadcrumbMatches,
    BreadcrumbContext,
    (match: UIMatch<unknown, HandleType>): UIMatch<unknown, HandleType> => {
      if (match.handle.route.id === routeId && !match.handle.route.title.includes(title)) {
        let _title = match.handle.route.title;

        if (_title.includes(title)) {
          match.handle.route.title = _title;
        } else {
          switch (mode) {
            case "prepend":
              match.handle.route.title = title + ` ${BreadcrumbStringSeperator} ` + match.handle.route.title;
              break;
            case "append":
              match.handle.route.title = match.handle.route.title  + ` ${BreadcrumbStringSeperator} ` + title;
              break;
            case "replace":
            default:
              match.handle.route.title = title;
          }
        }
      }

      return match;
    }
  );
}

/**
 * getBreadcrumb
 * @param routeId
 * @param mode
 */
export const getBreadcrumb = (routeId: string, mode?: "clean"): string => {
  let breadcrumbs: BreadcrumbType[] = BreadcrumbContext.getBreadcrumbs(),
    placeholder: string = "...";

  let filteredBreadcrumb = breadcrumbs.filter((breadcrumb: BreadcrumbType)=> breadcrumb.id === routeId);
  if (filteredBreadcrumb && filteredBreadcrumb[0]) {
    let label = filteredBreadcrumb[0].label;

    // clean mode? we split the label by string separator to get
    // only the dynamic string without all static title string appended or prepended
    return mode === "clean" ? String(label?.split(BreadcrumbStringSeperator).pop()).trim() : label || placeholder;
  }

  return placeholder;
}

/**
 * breadcrumbElements
 * @constructor
 */
const Breadcrumb = memo(() => {
  BreadcrumbContext = useContext(AppContext);
  BreadcrumbMatches = useMatches();

  React.useEffect(() => {
    processBreadcrumb(BreadcrumbMatches, BreadcrumbContext);
  // eslint-disable-next-line
  }, [BreadcrumbMatches]);

  // the crumb label is already the page name, dynamic segments included, so it
  // names the tab too. it has to go through Helmet: writing document.title
  // directly raced Helmet, which re-applied its defaultTitle ("DIA Interlink")
  // on the next unrelated render, e.g. after an error state or a detail reload
  const crumbs = BreadcrumbContext.getBreadcrumbs(),
    activeCrumb = crumbs[crumbs.length - 1],
    tabTitle = activeCrumb && typeof activeCrumb.label === "string" ? activeCrumb.label.trim() : "";

  return (
    <dia-breadcrumb>
      { /* no conditional inside <Helmet>: a boolean child makes
           react-helmet-async drop the whole block */ }
      { tabTitle !== "" && (
        <Helmet>
          <title>{ tabTitle }</title>
        </Helmet>
      ) }
      <nav aria-label="Breadcrumb">
        <ul>
          { renderBreadcrumb(BreadcrumbContext) }
        </ul>
      </nav>
    </dia-breadcrumb>
  );
});

export default Breadcrumb;
